import { z } from 'zod';
import { IsoDate } from './common';
import { PublicUser, UserRole } from './me';

const Email = z.string().trim().toLowerCase().pipe(z.email());

/** `GET /users` : sélecteurs d'owner et filtres. */
export const UserSummary = z.object({ id: z.string(), name: z.string() });
export type UserSummary = z.infer<typeof UserSummary>;
export const UsersResponse = z.object({ items: z.array(UserSummary) });
export type UsersResponse = z.infer<typeof UsersResponse>;

/** `GET /admin/users`. */
export const AdminUser = PublicUser.extend({
  banned: z.boolean(),
  banReason: z.string().nullable(),
  createdAt: IsoDate,
  lastSessionAt: IsoDate.nullable(),
});
export type AdminUser = z.infer<typeof AdminUser>;
export const AdminUsersResponse = z.object({ items: z.array(AdminUser) });
export type AdminUsersResponse = z.infer<typeof AdminUsersResponse>;

export const SetRoleBody = z.object({ role: UserRole });
export type SetRoleBody = z.infer<typeof SetRoleBody>;

export const BanUserBody = z.object({
  banned: z.boolean(),
  reason: z.string().trim().min(1).max(500).optional(),
});
export type BanUserBody = z.infer<typeof BanUserBody>;

/** `POST /admin/users/:id/set-password` : mot de passe temporaire renvoyé une seule fois. */
export const SetPasswordResponse = z.object({ temporaryPassword: z.string() });
export type SetPasswordResponse = z.infer<typeof SetPasswordResponse>;

export const CreateInvitationBody = z.object({
  email: Email,
  role: UserRole.default('member'),
  sendEmail: z.boolean().default(false),
});
export type CreateInvitationBody = z.infer<typeof CreateInvitationBody>;

export const CreateInvitationResponse = z.object({
  id: z.string(),
  email: z.string(),
  role: UserRole,
  expiresAt: IsoDate,
  /** Renvoyée uniquement à la création : le token n'est pas stocké. */
  inviteUrl: z.string(),
  emailSent: z.boolean(),
});
export type CreateInvitationResponse = z.infer<typeof CreateInvitationResponse>;

export const Invitation = z.object({
  id: z.string(),
  email: z.string(),
  role: UserRole,
  expiresAt: IsoDate,
  createdAt: IsoDate,
  expired: z.boolean(),
  invitedBy: UserSummary.nullable(),
});
export type Invitation = z.infer<typeof Invitation>;
export const InvitationsResponse = z.object({ items: z.array(Invitation) });
export type InvitationsResponse = z.infer<typeof InvitationsResponse>;

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export const AcceptInvitationBody = z.object({
  token: z.string().min(16).max(256),
  name: z.string().trim().min(2).max(80),
  password: z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH),
});
export type AcceptInvitationBody = z.infer<typeof AcceptInvitationBody>;

export const AcceptInvitationResponse = z.object({ user: PublicUser });
export type AcceptInvitationResponse = z.infer<typeof AcceptInvitationResponse>;
